import {FigureType} from "../types/enums";
import {Correlation, HighlightTarget, PointInfo, Score, TextCleanup} from "../types/list";

/** The figure types that support highlighting */
export const HIGHLIGHT_FIGURE_TYPES: readonly FigureType[] = [
  FigureType.ScoreAll, FigureType.ScoreQuery, FigureType.ScoreLibrary,
  FigureType.CorrelationSubnetworkG1, FigureType.CorrelationSubnetworkG2,
];

/** Figures with specific style of highlighting */
const SUBNETWORK_TYPES: readonly FigureType[] = [
  FigureType.CorrelationSubnetworkG1, FigureType.CorrelationSubnetworkG2,
];

/** Derives the genes to highlight from the selected row, including annotation offsets. */
function buildTargets(figureType: FigureType, row: Score | Correlation): HighlightTarget[] {
  if (SUBNETWORK_TYPES.includes(figureType)) {
    const corr = row as Correlation;

    return [
      {id: corr.g1_eid, name: corr.g1_cell.symbol},
      {id: corr.g2_eid, name: corr.g2_cell.symbol},
    ];
  }

  const score = row as Score;

  if (figureType === FigureType.ScoreLibrary) {
    return [{
      id: score.screen_cell.screen ?? score.screen.split(/\s+/)[1],
      name: score.screen,
      ax: 30, ay: -20,
    }];
  }

  return [{
    id: +score.gene_entrez.split(',')[0],
    name: score.library_cell.symbol,
    ax: 30, ay: -20,
  }];
}

/** Resolves a marker's rendered pixel size, accounting for sizeref/sizemode scaling. */
function markerPixelSize(marker: any, idx: number): number {
  const raw = Array.isArray(marker?.size) ? marker.size[idx] : (marker?.size ?? 8);
  const sizeref = marker?.sizeref ?? 1;

  return marker?.sizemode === 'area'
    ? Math.sqrt(raw / sizeref)
    : raw / sizeref;
}

/** Searches trace customdata for a matching id (entrez number or screen string) and returns its plot position. */
function findPoint(traces: any[], id: number | string): PointInfo | null {
  const match = typeof id === 'number'
    ? (val: any) => +val === id
    : (val: any) => val === id;

  for (let i = 0; i < traces.length; i++) {
    const trace = traces[i];
    const customdata: any[] = trace.customdata ?? [];
    const idx = customdata.findIndex(match);

    if (idx !== -1) return {
      trace,
      x: trace.x[idx],
      y: trace.y[idx],
      size: markerPixelSize(trace.marker, idx),
      traceIdx: i,
      pointIdx: idx,
    };
  }

  return null;
}

/**
 * Restores bolded text labels, removes highlight traces, and returns remaining base annotations.
 * Text is restored before trace deletion to keep trace indices stable.
 */
function clearHighlights(plotEl: any, Plotly: any): any[] {
  const textCleanups: TextCleanup[] = plotEl._highlightTextCleanups ?? [];

  for (const {traceIdx, pointIdx, original} of textCleanups) {
    const trace = plotEl.data[traceIdx];

    if (Array.isArray(trace?.text)) {
      const text = [...trace.text];
      text[pointIdx] = original;
      Plotly.restyle(plotEl, {text: [text]}, [traceIdx]);
    }
  }

  plotEl._highlightTextCleanups = [];

  const highlightIndices = (plotEl.data as any[])
    .map((_, i) => i)
    .filter(i => plotEl.data[i].meta?.isHighlight === true)
    .reverse();

  if (highlightIndices.length > 0) Plotly.deleteTraces(plotEl, highlightIndices);

  return (plotEl.layout?.annotations ?? []).filter((a: any) => !a._highlight);
}

/**
 * Applies a highlight ring and label to the given row's gene(s) on the plot.
 * Returns a cleanup function that cancels the pending rAF if called before it fires.
 */
export function applyHighlight(
  containerEl: HTMLElement | null,
  figureType: FigureType,
  row: Score | Correlation | null,
  fontSize: number = 12,
): () => void {
  const isSubnetwork = SUBNETWORK_TYPES.includes(figureType);

  const frame = requestAnimationFrame(() => {
    const plotEl = containerEl?.querySelector('.js-plotly-plot') as any;
    const Plotly = (window as any).Plotly;
    if (!plotEl?.data || !Plotly) return;

    const baseAnnotations = clearHighlights(plotEl, Plotly);

    if (!row) {
      Plotly.relayout(plotEl, {annotations: baseAnnotations});
      return;
    }

    const targets = buildTargets(figureType, row);

    // Snapshot before adding traces so the loop doesn't see newly added highlights.
    const tracesSnapshot = [...plotEl.data];
    const newAnnotations = [];

    for (const {id, name, ax, ay} of targets) {
      const point = findPoint(tracesSnapshot, id);
      if (!point) continue;

      Plotly.addTraces(plotEl, {
        name,
        x: [point.x],
        y: [point.y],
        mode: 'markers',
        type: 'scattergl',
        showlegend: false,
        hoverinfo: 'skip',
        meta: {
          isHighlight: true
        },
        marker: {
          color: 'rgba(0,0,0,0)',
          size: point.size,
          line: {
            color: '#FF0000',
            width: 3
          }
        },
      });

      if (isSubnetwork) {
        // Bold the on-node text label instead of adding a floating annotation.
        if (Array.isArray(point.trace.text)) {
          const original = String(point.trace.text[point.pointIdx] ?? '');
          const text = [...point.trace.text];

          text[point.pointIdx] = `<b>${original}</b>`;
          Plotly.restyle(plotEl, {text: [text]}, [point.traceIdx]);
          (plotEl._highlightTextCleanups ??= []).push({
            original,
            traceIdx: point.traceIdx,
            pointIdx: point.pointIdx,
          });
        }
      } else {
        const rawText = Array.isArray(point.trace.text) ? point.trace.text[point.pointIdx] : name;
        // Strip away the stats part of the label. It always starts with a number, negative or positive in parentheses
        const nodeText = typeof rawText === 'string'
          ? rawText.replace(/ \(-?\d.*$/, '')
          : rawText;

        newAnnotations.push({
          ax,
          ay,
          x: point.x,
          y: point.y,
          text: nodeText,
          _highlight: true,
          showarrow: true,
          arrowhead: 2,
          arrowcolor: 'black',
          arrowwidth: 1,
          font: {
            size: fontSize,
            color: 'black'
          },
          bgcolor: 'white',
          bordercolor: 'black',
          borderwidth: 1,
          borderpad: 5,
          opacity: 0.85,
        });
      }
    }

    Plotly.relayout(plotEl, {annotations: [...baseAnnotations, ...newAnnotations]});
  });

  return () => cancelAnimationFrame(frame);
}